// Diálogo para que el Fiscal gestione delegados con acceso al Dashboard Ejecutivo.
// Patrón análogo al de Panel_Firma_Delegados.

import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, IconButton, Typography, Box, Autocomplete, TextField,
    List, ListItem, ListItemText, ListItemSecondaryAction, Chip, Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupsIcon from '@mui/icons-material/Groups';
import {
    dashboardEjecutivoDelegadosJWT,
    dashboardEjecutivoAgregarDelegadoJWT,
    dashboardEjecutivoQuitarDelegadoJWT,
    personalBuscarRapidoJWT,
} from '../../api/endpoints';

export default function DelegadosDialog({ open, onClose }) {
    const [delegados, setDelegados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busqueda, setBusqueda] = useState('');
    const [opciones, setOpciones] = useState([]);
    const [seleccionado, setSeleccionado] = useState(null);
    const [agregando, setAgregando] = useState(false);
    const [error, setError] = useState('');

    const cargar = async () => {
        setLoading(true);
        try {
            const r = await dashboardEjecutivoDelegadosJWT();
            if (r?.ok) setDelegados(r.delegados || []);
        } finally { setLoading(false); }
    };

    useEffect(() => { if (open) { cargar(); setError(''); setSeleccionado(null); } }, [open]);

    // Búsqueda de personal con debounce simple
    useEffect(() => {
        if (!busqueda || busqueda.length < 2) { setOpciones([]); return; }
        let cancel = false;
        const t = setTimeout(async () => {
            try {
                const r = await personalBuscarRapidoJWT(busqueda, 15);
                if (!cancel) setOpciones(r?.items || []);
            } catch { /* ignore */ }
        }, 300);
        return () => { cancel = true; clearTimeout(t); };
    }, [busqueda]);

    const agregar = async () => {
        if (!seleccionado) return;
        setAgregando(true);
        setError('');
        try {
            const r = await dashboardEjecutivoAgregarDelegadoJWT(seleccionado.Id);
            if (r?.ok) {
                setSeleccionado(null);
                setBusqueda('');
                cargar();
            } else {
                setError(r?.mensaje || 'No se pudo agregar');
            }
        } catch (e) {
            setError(e?.message || 'Error');
        } finally { setAgregando(false); }
    };

    const quitar = async (idPersonal) => {
        try {
            await dashboardEjecutivoQuitarDelegadoJWT(idPersonal);
            cargar();
        } catch (e) { setError(e?.message || 'Error'); }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '4px' } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1769aa' }}>
                <GroupsIcon /> Delegados del Dashboard Ejecutivo
            </DialogTitle>
            <DialogContent dividers>
                <Alert severity="info" sx={{ mb: 2 }}>
                    Los delegados pueden ver el Dashboard Ejecutivo con la misma información que vos.
                    Ideal para Sub-Director, Director Administrativo o Asesor de confianza.
                </Alert>

                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Agregar delegado</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-start' }}>
                    <Autocomplete
                        options={opciones}
                        value={seleccionado}
                        onChange={(_, val) => setSeleccionado(val)}
                        inputValue={busqueda}
                        onInputChange={(_, val) => setBusqueda(val)}
                        getOptionLabel={(o) => o ? `${o.Apellido}, ${o.Nombre} (${o.Id})` : ''}
                        isOptionEqualToValue={(a, b) => a?.Id === b?.Id}
                        filterOptions={(x) => x}
                        size="small"
                        sx={{ flex: 1 }}
                        renderInput={(params) => (
                            <TextField {...params} placeholder="Buscar por apellido, nombre o DNI..." />
                        )}
                    />
                    <Button variant="contained" onClick={agregar} disabled={!seleccionado || agregando}>
                        {agregando ? 'Agregando...' : 'Agregar'}
                    </Button>
                </Box>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                    Delegados activos ({delegados.length})
                </Typography>
                {loading ? (
                    <Typography color="text.secondary">Cargando...</Typography>
                ) : delegados.length === 0 ? (
                    <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No hay delegados. Solo vos tenés acceso al Dashboard Ejecutivo.
                    </Typography>
                ) : (
                    <List dense sx={{ border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                        {delegados.map(d => (
                            <ListItem key={d.Id} divider>
                                <ListItemText
                                    primary={<Typography sx={{ fontWeight: 500 }}>{d.Apellido}, {d.Nombre}</Typography>}
                                    secondary={
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                            {d.Funcion && <Chip size="small" label={d.Funcion} variant="outlined" />}
                                            <Typography variant="caption" color="text.secondary">
                                                Agregado por {d.Agregado_Por || 'sistema'} · {new Date(d.Fecha_Alta).toLocaleDateString('es-AR')}
                                            </Typography>
                                        </Box>
                                    }
                                />
                                <ListItemSecondaryAction>
                                    <IconButton edge="end" size="small" onClick={() => quitar(d.Id_Personal)}>
                                        <DeleteIcon fontSize="small" sx={{ color: '#c62828' }} />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
}
